import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import {
  AIDE_GOV_ID_TYPE_KEYS,
  AideGovIdType,
  AideIdentityData,
  emptyLiveInAideState,
} from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 1 — Confirm identity for the Live-In Aide (RESPROP LIA Questionnaire
// p1). Mirrors the CoApplicantIdentity DOB/SSN/govId widget patterns (same
// masking, same gov-ID option list). Adds aide-specific fields: home/mobile
// phone, "Name of Resident Who Will Receive Services", and the two exempt-status
// checkboxes. Name + email are prefilled read-only from the aide's roster stub
// (when the container supplies them) and are NOT part of the editable payload.
// ---------------------------------------------------------------------------

/** Read-only identity fields prefilled from the aide's application / person. */
export interface AidePrefill {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
}

interface Props {
  /** Persisted identity slice (state.identity). */
  defaultValues?: Partial<AideIdentityData>;
  /** Read-only name/email prefill from the roster stub / invited person. */
  prefill?: AidePrefill;
  onSubmit: (data: AideIdentityData) => void | Promise<void>;
  isSaving?: boolean;
}

const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;

function dobToDateInput(value: string | undefined): string {
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? "");
  if (!us) return "";
  const month = Number(us[1]);
  const day = Number(us[2]);
  const year = Number(us[3]);
  const date = new Date(year, month - 1, day);
  if (
    month < 1 ||
    month > 12 ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "";
  }
  return `${us[3]}-${us[1]}-${us[2]}`;
}

function dateInputToDob(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!iso) return "";
  return `${iso[2]}/${iso[3]}/${iso[1]}`;
}

function dobInputMax(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

const formatSsn = (value: string): string =>
  value.replace(/\D/g, "").slice(0, 9);

export const AideIdentity: React.FC<Props> = ({
  defaultValues,
  prefill,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const schema = yup.object({
    dob: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.dobRequired") as string
      )
      .matches(
        dobRegex,
        t("affordable.application.memberProfiles.dobError") as string
      ),
    noSsn: yup.boolean().default(false),
    ssn: yup.string().when("noSsn", {
      is: false,
      then: (s) =>
        s
          .required(
            t("affordable.application.memberProfiles.ssnRequired") as string
          )
          .matches(
            /^\d{9}$/,
            t("affordable.application.memberProfiles.ssnDigits") as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    govIdType: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdTypeRequired") as string
      ),
    govId: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdRequired") as string
      ),
    residentServedName: yup
      .string()
      .required(
        t(
          "affordable.application.liveInAide.identity.residentServedRequired"
        ) as string
      ),
    homePhone: yup.string().notRequired(),
    mobilePhone: yup.string().notRequired(),
    exemptNonCitizen: yup.boolean().default(false),
    exemptSenior: yup.boolean().default(false),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AideIdentityData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      dob: defaultValues?.dob ?? emptyLiveInAideState.identity.dob,
      ssn: defaultValues?.ssn ?? emptyLiveInAideState.identity.ssn,
      noSsn: defaultValues?.noSsn ?? emptyLiveInAideState.identity.noSsn,
      govIdType:
        defaultValues?.govIdType ?? emptyLiveInAideState.identity.govIdType,
      govId: defaultValues?.govId ?? emptyLiveInAideState.identity.govId,
      homePhone:
        defaultValues?.homePhone ?? emptyLiveInAideState.identity.homePhone,
      mobilePhone:
        defaultValues?.mobilePhone ?? emptyLiveInAideState.identity.mobilePhone,
      residentServedName:
        defaultValues?.residentServedName ??
        emptyLiveInAideState.identity.residentServedName,
      exemptNonCitizen:
        defaultValues?.exemptNonCitizen ??
        emptyLiveInAideState.identity.exemptNonCitizen,
      exemptSenior:
        defaultValues?.exemptSenior ??
        emptyLiveInAideState.identity.exemptSenior,
    },
  });

  const noSsn = watch("noSsn");
  const prefillName = [
    prefill?.firstName,
    prefill?.middleName,
    prefill?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.identity.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.identity.subtitle")}
        </Typography>

        <Grid container spacing={2}>
          {/* Prefilled read-only identity (from the roster stub). Rendered only
              when the container supplies it; omitted gracefully otherwise. */}
          {prefillName && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("formInput.firstName") as string}
                value={prefillName}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-readonly": true }}
              />
            </Grid>
          )}
          {prefill?.email && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("formInput.email") as string}
                value={prefill.email}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-readonly": true }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  fullWidth
                  value={dobToDateInput(field.value)}
                  onChange={(e) =>
                    field.onChange(dateInputToDob(e.target.value))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: dobInputMax() }}
                  label={`${t("account.leaseApplication.birthDate")} *`}
                  error={!!errors.dob}
                  helperText={errors.dob?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="ssn"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={
                    t(
                      "affordable.application.memberProfiles.ssnLabel"
                    ) as string
                  }
                  type="password"
                  disabled={noSsn}
                  inputProps={{ inputMode: "numeric", maxLength: 9 }}
                  error={!!errors.ssn}
                  helperText={errors.ssn?.message}
                  onChange={(e) => field.onChange(formatSsn(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="noSsn"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        field.onChange(checked);
                        if (checked) {
                          // Clear the SSN when the aide declares they have none.
                          setValue("ssn", "");
                        } else {
                          // Exempt status only applies when there is no SSN;
                          // clear it so hidden values can't leak into the payload.
                          setValue("exemptNonCitizen", false);
                          setValue("exemptSenior", false);
                        }
                      }}
                    />
                  }
                  label={t("account.leaseApplication.noSsn") as string}
                />
              )}
            />
          </Grid>

          {/* Government-issued ID — same option list + section label as the
              co-applicant / head-of-household forms. */}
          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {t("affordable.application.memberProfiles.idSectionLabel")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="govIdType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.govIdType}>
                  <InputLabel>
                    {`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  </InputLabel>
                  <Select
                    {...field}
                    label={`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {AIDE_GOV_ID_TYPE_KEYS.map((key: AideGovIdType) => (
                      <MenuItem key={key} value={key}>
                        {t(
                          `affordable.application.memberProfiles.govIdTypes.${key}`
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.govIdType && (
                    <FormHelperText>
                      {errors.govIdType.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller
              name="govId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.memberProfiles.govIdLabel"
                  )} *`}
                  inputProps={{ autoComplete: "off" }}
                  error={!!errors.govId}
                  helperText={errors.govId?.message}
                />
              )}
            />
          </Grid>

          {/* Contact phones — both optional per the questionnaire. */}
          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {t("affordable.application.liveInAide.identity.contactLabel")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="homePhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={
                    t(
                      "affordable.application.liveInAide.identity.homePhoneLabel"
                    ) as string
                  }
                  inputProps={{ inputMode: "tel" }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="mobilePhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={
                    t(
                      "affordable.application.liveInAide.identity.mobilePhoneLabel"
                    ) as string
                  }
                  inputProps={{ inputMode: "tel" }}
                />
              )}
            />
          </Grid>

          {/* Name of Resident Who Will Receive Services (required). */}
          <Grid item xs={12}>
            <Controller
              name="residentServedName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.liveInAide.identity.residentServedLabel"
                  )} *`}
                  error={!!errors.residentServedName}
                  helperText={errors.residentServedName?.message}
                />
              )}
            />
          </Grid>

          {/* Exempt-status checkboxes (both optional). Shown only when the aide
              has declared they have no SSN — these are the two HUD categories
              exempt from the SSN-disclosure requirement (24 CFR 5.216). */}
          {noSsn && (
            <Grid item xs={12}>
              <Typography
                color="primary"
                sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}
              >
                {t("affordable.application.liveInAide.identity.exemptLabel")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 1 }}
              >
                {t("affordable.application.liveInAide.identity.exemptCaption")}
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Controller
                  name="exemptNonCitizen"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        t(
                          "affordable.application.liveInAide.identity.exemptNonCitizen"
                        ) as string
                      }
                    />
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", ml: 4 }}
                >
                  {t(
                    "affordable.application.liveInAide.identity.exemptNonCitizenHelp"
                  )}
                </Typography>
              </Box>
              <Box>
                <Controller
                  name="exemptSenior"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        t(
                          "affordable.application.liveInAide.identity.exemptSenior"
                        ) as string
                      }
                    />
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", ml: 4 }}
                >
                  {t(
                    "affordable.application.liveInAide.identity.exemptSeniorHelp"
                  )}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
